import {getCountry, useConfig} from '@aboutyou/config';
import {useTranslation} from '@aboutyou/i18n';
import {useOpenedStateManager} from '@aboutyou/opened-state-manager';
import {
  COLORS,
  ContentPopper,
  ContentPopperProps,
  FONT_SIZES,
  FONT_WEIGHTS,
  RADIUS,
  SPACING,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import {useScaylePaymentMethodState} from '../../scayle-state/useScaylePaymentMethodState';
import {InvoiceProcessFlow} from './InvoiceProcessFlow';
import {MarketGroup, getMarketGroup} from './constants';

export const usePopover = (key: string) => {
  const {isOpen, open, cancel} = useOpenedStateManager(key);

  const openPopover = () => open();
  const closePopover = () => cancel();

  return {isOpen, openPopover, closePopover};
};

const HowDoesItWorkBody = () => {
  const {t} = useTranslation();

  return (
    <BodyWrapper>
      <SectionHeadline>{t('Invoice')}</SectionHeadline>
      <InvoiceProcessFlow />
      <SectionText>
        <SectionHeadline>{t('How it works')}</SectionHeadline>

        <SectionParagraphs>
          <BodyText
            dangerouslySetInnerHTML={{
              __html: t(
                'Once we have <b>shipped your order</b>, you can take <b>14 days to pay</b>. During this time, you can try the items you ordered and decide what you want to keep.',
              ),
            }}
          />
          <BodyText
            dangerouslySetInnerHTML={{
              __html: t(
                'You will receive a <b>payment link</b> from us. Once you have decided: select the items you wish to keep and we will <b>update your invoice amount</b>.',
              ),
            }}
          />
          <BodyText>
            {t('Select your preferred payment method. Pay. Done!')}
          </BodyText>
        </SectionParagraphs>
      </SectionText>
      <SectionText>
        <SectionHeadline>{t("In case you're wondering...")}</SectionHeadline>
        <SectionParagraphs>
          <BodyText>
            {t(
              "You can return your items as usual. Our return policy still applies to all payment methods. Don't worry, we'll remind you via email when your payment is due. Just to be on the safe side.",
            )}
          </BodyText>
        </SectionParagraphs>
      </SectionText>
    </BodyWrapper>
  );
};

export const HowDoesItWorkModal = ({
  open,
  onClose,
  LayoutComponent,
}: {
  open: boolean;
  onClose: () => void;
  LayoutComponent: ContentPopperProps['LayoutComponent'];
}) => {
  const {t} = useTranslation();

  return (
    <ContentPopper
      LayoutComponent={LayoutComponent}
      open={open}
      onClose={onClose}
      headerContent={t('How does it work?')}
      bodyContent={<HowDoesItWorkBody />}
      footerContent={
        <CloseButton onClick={onClose} data-testid="how-does-it-work-close-btn">
          {t('Close')}
        </CloseButton>
      }
      renderInPortal
      data-testid="how-does-it-work"
    />
  );
};

const WhyNotBody = () => {
  const {t} = useTranslation();

  return (
    <BodyWrapper>
      <BodyText>
        {t(
          'We are sorry that payment by invoice is not possible here. Since we effectively advance the invoice amounts until you have transferred them to us, we check with every order anew how secure this is for us, but also for you. For example, we want to rule out the possibility of someone else ordering something to their address, or literally on your invoice.',
        )}
      </BodyText>
      <BodyText>
        {t(
          'There can be various reasons why purchase on account is not possible for this order:',
        )}
      </BodyText>
      <BulletList>
        <BulletItem
          dangerouslySetInnerHTML={{
            __html: t('<b>Delivery and billing addresses do not match.</b>'),
          }}
        />
        <BulletItem
          dangerouslySetInnerHTML={{
            __html: t(
              '<b>Have you recently moved or got married?</b> (Then congratulations!) If something does not fit together for us when comparing name and address, we exclude payment by invoice for your security.',
            ),
          }}
        />
        <BulletItem
          dangerouslySetInnerHTML={{
            __html: t(
              '<b>The products in your shopping basket exceed the maximum order value</b> for purchase on account. For example, because it is simply very high or one of your returns has not yet been received by us.',
            ),
          }}
        />
        <BulletItem
          dangerouslySetInnerHTML={{
            __html: t(
              '<b>You still have an old invoice open with us</b> – which sometimes simply means that a transfer has not yet been credited to our account.',
            ),
          }}
        />
        <BulletItem
          dangerouslySetInnerHTML={{
            __html: t(
              '<b>Credit checks:</b> Like other platforms and shops, we use independent credit agencies to help us assess your creditworthiness. In some cases, their data may mean that you cannot pay by invoice for your order.',
            ),
          }}
        />
      </BulletList>
      <BodyText>
        {t(
          "We reserve the right to refuse a payment method for certain orders. If you have any questions or concerns about the payment methods offered to you, you will find further information in the 'Help & Contact' section as well as information on how to contact us.",
        )}
      </BodyText>
    </BodyWrapper>
  );
};

export const WhyNotModal = ({
  open,
  onClose,
  LayoutComponent,
}: {
  open: boolean;
  onClose: () => void;
  LayoutComponent: ContentPopperProps['LayoutComponent'];
}) => {
  const {t} = useTranslation();

  return (
    <ContentPopper
      LayoutComponent={LayoutComponent}
      open={open}
      onClose={onClose}
      headerContent={t('Payment by invoice not possible?')}
      bodyContent={<WhyNotBody />}
      footerContent={
        <CloseButton onClick={onClose} data-testid="why-not-close-btn">
          {t('Close')}
        </CloseButton>
      }
      renderInPortal
      data-testid="why-not"
    />
  );
};

const LearnMorePopoverBody = () => {
  const {locale} = useConfig();
  const {t} = useTranslation();
  const countryCode = getCountry(locale);
  const marketGroup = getMarketGroup(countryCode);
  const {hasBnplConsentCheckbox} = useScaylePaymentMethodState();

  if (hasBnplConsentCheckbox) {
    return (
      <BodyWrapper>
        <BodyText>
          {t(
            'In order to assess my creditworthiness I authorize Zalando to share my personal data collected in the application process as an identifier with Krajowe Biuro Informacji Gospodarczej S.A. with its seat in Lublańska 34 Street, 31 – 476 Kraków ("KBIG") and ERIF Biuro Informacji Gospodarczej S.A. with its seat in Jerozolimskie 100 Avenues, 00 – 807 Warszawa ("ERIF"). I further authorize KBIG and ERIF to receive such identifier and share with Zalando data concerning my liabilities, pursuant to Article 24 Paragraph 1 of the Act of April 9, 2010, on providing access to business information and exchange of business data:',
          )}
        </BodyText>
        <BulletList>
          <BulletItem>
            {t(
              'Entities cooperating within the Credit Information Exchange Platform and the Information Exchange System*, in order to obtain from them information regarding the history of my financial obligations.',
            )}
          </BulletItem>
          <BulletItem>
            {t(
              'Entities cooperating within the framework of the Credit Information Exchange Platform and the Information Exchange System*, to make available to Zalando, within 30 days, information regarding the history of my financial obligations in order for Zalando to assess my credit rating.',
            )}
          </BulletItem>
          <BulletItem>
            {t(
              'Pursuant to Article 24 Paragraph 1 of the Act of April 9, 2010 on providing access to business information and exchange of business data, I authorize Zalando to apply to the National Economic Information Bureau S.A. with its seat in Krakow, Lublańska 38 Street, 31-476 Krakow to disclose business information regarding my liabilities.',
            )}
          </BulletItem>
          <BulletItem>
            {t(
              'Entities cooperating with the Information Exchange System*, in order to enable them to enrich the contact data of consumers on an ongoing and cyclical basis, in connection with the enforcement of debts owed to them by their customers, and to verify the correctness of the enrichment.',
            )}
          </BulletItem>
        </BulletList>
        <BodyText>
          {t(
            "*Details for the Information Exchange System and the list of entities cooperating within its framework, together with their contact details can be found in Chapter 17.13 of Zalando's Privacy Notice.",
          )}
        </BodyText>
      </BodyWrapper>
    );
  }

  const serviceProvider =
    marketGroup === MarketGroup.Nordics
      ? t('Creditsafe i Sverige AB, Box 320, 401 25 Göteborg')
      : t('CRIF GmbH, Leopoldstraße 244, 80802 München');

  return (
    <BodyWrapper>
      <SectionText>
        <SectionHeadline>{t('What is this?')}</SectionHeadline>
        <BodyText>
          {t(
            'In order to offer you this payment method, we need to check your eligibility. Please see our Privacy Notice for more details.',
          )}
        </BodyText>
      </SectionText>
      <SectionText>
        <SectionHeadline>{t('How it works')}</SectionHeadline>
        <BodyText>
          {t(
            'To do this we will share your first name, surname, address, email, date of birth with the following service provider:',
          )}
          {'\n'}
          {serviceProvider}
        </BodyText>
      </SectionText>
    </BodyWrapper>
  );
};

export const LearnMorePopover = ({
  open,
  onClose,
  LayoutComponent,
}: {
  open: boolean;
  onClose: () => void;
  LayoutComponent: ContentPopperProps['LayoutComponent'];
}) => {
  const {t} = useTranslation();

  return (
    <ContentPopper
      LayoutComponent={LayoutComponent}
      open={open}
      onClose={onClose}
      headerContent={t('Learn more')}
      bodyContent={<LearnMorePopoverBody />}
      footerContent={
        <CloseButton onClick={onClose} data-testid="learn-more-close-btn">
          {t('Close')}
        </CloseButton>
      }
      renderInPortal
      data-testid="learn-more-popover"
    />
  );
};

const SectionText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING['Y.Sm']};
`;

const SectionParagraphs = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING['Y.Md']};
`;

const BodyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING['Y.Lg']};
`;

const BodyText = styled.p`
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  line-height: 1.5;
  color: ${COLORS.Black};
  margin: 0;
`;

const SectionHeadline = styled.p`
  font-size: ${FONT_SIZES.px18};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  line-height: 1.5;
  color: ${COLORS.Black};
  margin: 0;
`;

const BulletList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${SPACING['Y.Md']};
  margin: 0;
  padding: 0;
  list-style: disc;
  padding-left: calc(${SPACING['X.Lg']} + ${SPACING['X.Sm']});
`;

const BulletItem = styled.li`
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  line-height: 1.5;
  color: ${COLORS.Black};
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: ${SPACING['Y.Xl4']};
  background: ${COLORS.White};
  border: 1px solid ${COLORS['Grey.Shade300']};
  border-radius: ${RADIUS.Sm};
  cursor: pointer;
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  line-height: 1.5;
  color: ${COLORS.Black};
  padding: 0 24px;
`;
